import { useWorkspaceStore } from '@/store/workspaceStore';
import { exportProject, ExportError, type ExportHandle } from '@/lib/render/exportProject';
import { synthesizeVoiceover, TtsApiError } from '@/lib/api/ttsClient';
import { resolveEffectiveSettings } from '@/lib/effectiveSettings';
import { storeVideoBlob } from '@/lib/media';
import { buildFileName } from '@/lib/filename';
import { validateVoiceoverTiming } from '@/lib/validation';
import type { RenderJob } from '@/types';

/** In-flight cancel handles, keyed by job id — lets the UI cancel a specific running render. */
const activeHandles = new Map<string, ExportHandle>();
let isProcessing = false;

export function cancelJob(jobId: string): void {
  activeHandles.get(jobId)?.cancel();
}

export function isJobActive(jobId: string): boolean {
  return activeHandles.has(jobId);
}

/**
 * Processes every `queued` job in the workspace queue, respecting `concurrency`
 * simultaneous renders so the browser is never asked to render everything at
 * once. Safe to call repeatedly (e.g. after adding new jobs) — it is a no-op
 * while already running, and future calls will pick up newly queued jobs once
 * the current pass finishes.
 */
export async function processQueue(concurrency = 1): Promise<void> {
  if (isProcessing) return;
  isProcessing = true;
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const store = useWorkspaceStore.getState();
      const pending = store.workspace.jobOrder
        .map((id) => store.workspace.jobs[id])
        .filter((job): job is RenderJob => !!job && job.status === 'queued');

      if (pending.length === 0) return;

      const batch = pending.slice(0, concurrency);
      await Promise.all(batch.map((job) => runJob(job.id)));
    }
  } finally {
    isProcessing = false;
  }
}

async function runJob(jobId: string): Promise<void> {
  const store = useWorkspaceStore.getState();
  const job = store.workspace.jobs[jobId];
  if (!job) return;
  const project = store.workspace.projects[job.projectId];

  if (!project) {
    store.updateJob(jobId, { status: 'failed', error: 'The Reel for this job no longer exists.' });
    return;
  }

  store.updateJob(jobId, { status: 'preparing-voiceover', progress: 1, error: null, startedAt: Date.now() });

  try {
    // Generate the voiceover automatically if a script exists but hasn't been synthesized yet.
    let currentProject = useWorkspaceStore.getState().workspace.projects[job.projectId] ?? project;
    const sharedDefaults = useWorkspaceStore.getState().workspace.shared;
    const effectiveScript = currentProject.voiceover.script.trim();

    if (effectiveScript && !currentProject.voiceover.generatedMediaId) {
      const vo = resolveEffectiveSettings(currentProject, sharedDefaults).voiceover;
      const result = await synthesizeVoiceover(
        vo.provider === 'google'
          ? { provider: 'google', text: effectiveScript, languageCode: vo.languageCode, voiceName: vo.voiceName, voiceGender: vo.voiceGender, speakingRate: vo.speakingRate, pitch: vo.pitch }
          : { provider: 'openai', text: effectiveScript, openaiVoice: vo.openaiVoice, openaiModel: vo.openaiModel, speakingRate: vo.speakingRate }
      );
      store.updateVoiceover(job.projectId, {
        generatedMediaId: result.asset.id,
        generatedDurationSeconds: result.durationSeconds,
        wordTimings: result.wordTimings,
        timingSource: result.timingSource,
      });
      currentProject = useWorkspaceStore.getState().workspace.projects[job.projectId]!;
    }

    const timingCheck = validateVoiceoverTiming(currentProject.voiceover);
    if (!timingCheck.valid) {
      throw new ExportError(`Voiceover timing is invalid: ${timingCheck.errors.join(' ')}`);
    }

    store.updateJob(jobId, { status: 'rendering', progress: 2 });

    const shared = useWorkspaceStore.getState().workspace.shared;
    const handle = await exportProject(currentProject, shared, {
      onProgress: (p) => {
        const overall = p.stage === 'done' ? 100 : Math.round(p.progress);
        useWorkspaceStore.getState().updateJob(jobId, { progress: overall });
      },
    });
    activeHandles.set(jobId, handle);

    const blob = await handle.promise;
    activeHandles.delete(jobId);

    const projectsInOrder = useWorkspaceStore.getState().workspace.projectOrder;
    const sequenceIndex = projectsInOrder.indexOf(job.projectId) + 1;
    const fileName = buildFileName({
      title: currentProject.title,
      exportSettings: currentProject.export.overridesShared ? currentProject.export : shared.export,
      sequenceIndex,
    });

    const asset = await storeVideoBlob(blob, fileName);
    store.updateJob(jobId, {
      status: 'complete',
      progress: 100,
      resultMediaId: asset.id,
      resultFileName: fileName,
      resultSizeBytes: asset.sizeBytes,
      completedAt: Date.now(),
    });
  } catch (err) {
    activeHandles.delete(jobId);
    if (err instanceof Error && err.name === 'RenderCancelledError') {
      store.updateJob(jobId, { status: 'cancelled', error: null });
      return;
    }
    const message =
      err instanceof ExportError || err instanceof TtsApiError
        ? err.message
        : err instanceof Error
          ? 'Rendering failed unexpectedly. Enable debug mode for technical details.'
          : 'Rendering failed unexpectedly.';
    if (useWorkspaceStore.getState().debugMode && err instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(`[Job ${jobId}] failed:`, err);
    }
    store.updateJob(jobId, { status: 'failed', error: message, completedAt: Date.now() });
  }
}

/** Resets a failed/cancelled job back to `queued` so processQueue will retry it. */
export function retryJob(jobId: string): void {
  useWorkspaceStore.getState().updateJob(jobId, { status: 'queued', progress: 0, error: null });
  void processQueue();
}
